import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'
import { useRunnerUITransition } from '@/composables/useRunnerUITransition'

type Mood = {
  path: string
}
const moods: Mood[] = [
  {
    path: 'M3 15.3C3 24.3 10.5 32.2 20 32.2C29.5 32.2 37 24.3 37 15.3',
  },
  {
    path: 'M3.3 22.2L20 24L36.8 25.7',
  },
  {
    path: 'M3 35.2C3 26.1 10.5 18.3 20 18.3C29.5 18.3 37 26.1 37 35.2',
  },
]

export default function RunnerJauge() {
  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const { gameState, persona, scenario } = useRunnerState()
  const [jauge, setJauge] = useState(0)
  const [currentMood, setCurrentMood] = useState(0)

  const [morphTimeline, setMorphTimeline] = useState<gsap.core.Timeline | null>(null)

  const mouthPathRef = useRef<SVGPathElement>(null)

  const [isVisible, setIsVisible] = useState(false)
  const segment = gameState.currentSegment
  const gameJauge = gameState.jauge

  useEffect(() => {
    if (persona === 'francesca' && scenario === 0) {
      if (segment === 'equation_1_a') {
        setIsVisible(true)
      }
    } else if (persona === 'francesca' && scenario === 1) {
      if (segment === 'shopping') {
        setIsVisible(true)
      }
    } else if (persona === 'caris' && scenario === 1) {
      if (segment == 'dance_1_1') {
        setIsVisible(true)
      }
    } else if (persona === 'caris' && scenario === 0) {
      if (segment == 'supports') {
        setIsVisible(true)
      }
    } else {
      setIsVisible(false)
    }
  }, [persona, segment, scenario])

  useEffect(() => {
    const ctx = gsap.context(() => {
      const progress = {
        value: jauge,
      }
      gsap.to(progress, {
        value: gameJauge,
        duration: 0.666,
        ease: 'quad.inOut',
        onUpdate: () => {
          setJauge(progress.value)
          setCurrentMood(progress.value < 0.33 ? 0 : progress.value < 0.66 ? 1 : 2)
        },
      })
    })

    return () => ctx?.kill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameJauge, gsap, setJauge, setCurrentMood])

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (mouthPathRef.current === null || !isVisible) return
      const tl = gsap
        .timeline({ paused: true })
        .fromTo(
          mouthPathRef.current,
          {
            morphSVG: moods[0].path,
          },
          {
            morphSVG: moods[1].path,
            duration: 1,
            ease: 'quad.inOut',
          }
        )
        .to(mouthPathRef.current, {
          morphSVG: moods[2].path,
          duration: 1,
          ease: 'quad.inOut',
        })

      setMorphTimeline(tl)
    })

    return () => ctx?.revert()
  }, [gsap, mouthPathRef, isVisible, setMorphTimeline])

  useEffect(() => {
    if (morphTimeline) {
      morphTimeline?.progress(jauge)
    }
  }, [jauge, morphTimeline])

  const elementRef = useRef<HTMLDivElement>(null)
  const { onEnter, onExit, timeout } = useRunnerUITransition(elementRef, { from: 'top' })

  return (
    <Transition
      in={isVisible}
      nodeRef={elementRef}
      onEnter={onEnter}
      onExit={onExit}
      mountOnEnter
      unmountOnExit
      timeout={timeout}
    >
      <div
        ref={elementRef}
        className='Jauge w-full h-48 rounded-12 relative self-start mt-[31px] pointer-events-none max-w-128'
      >
        <div className='absolute bottom-0 right-0 pb-44 rounded-16 bg-secondary border-2 border-secondary'>
          <div className='px-12 py-4 text-body-s'>{t(`runner.${persona}.${scenario + 1}.jauge.label`)}</div>
        </div>

        <div className='h-full min-w-48 rounded-12 overflow-hidden relative bg-tertiary border-2 border-secondary'>
          <div className='Jauge__Background absolute inset-0'>
            <div
              className='Jauge__Background__Progress absolute top-0 left-44 h-full w-[calc(100%-44px)]'
              style={{ transform: `translateX(${(1 - jauge) * -100}%)` }}
            >
              <div
                className={`absolute top-0 -left-44 right-0 bottom-0 rounded-12 transition-opacity duration-333 ease-out-quad bg-light-blue ${currentMood >= 0 ? 'opacity-100' : 'opacity-0'}`}
              />
              <div
                className={`absolute top-0 -left-44 right-0 bottom-0 rounded-12 transition-opacity duration-333 ease-out-quad bg-coral ${currentMood >= 1 ? 'opacity-100' : 'opacity-0'}`}
              />
              <div
                className={`absolute top-0 -left-44 right-0 bottom-0 rounded-12 transition-opacity duration-333 ease-out-quad bg-scarlet ${currentMood >= 2 ? 'opacity-100' : 'opacity-0'}`}
              />
            </div>
          </div>

          <div className='Jauge__Face relative w-full h-full px-28'>
            <div className='relative h-full w-1/2' style={{ transform: `translateX(${jauge * 100}%)` }}>
              <div className='absolute top-0 left-0 h-44 -ml-28 aspect-square flex items-center justify-center'>
                <svg className='w-32 h-32' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <circle cx='12.3284' cy='9.72475' r='4.22485' fill='currentColor' />
                  <circle cx='27.5393' cy='9.72475' r='4.22485' fill='currentColor' />
                  <path
                    ref={mouthPathRef}
                    d='M3 15.3C3 24.3 10.5 32.2 20 32.2C29.5 32.2 37 24.3 37 15.3'
                    stroke='currentColor'
                    fill='none'
                    strokeWidth='5'
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  )
}
